/*
 * Starting an examiner-administered session.
 *
 * Two calls in order: /session/create makes the session and hands back its
 * sessionID, then /session/startTest binds an examinee, label and test to it.
 * Only after the second does /examiner/test or /examiner/questionnaire have
 * anything to administer, so callers navigate on the resolved value.
 *
 * Shared by the Create Test Session page and the Invite Rater page's
 * "Administer Now", which differ only in which tests they offer and where they
 * navigate afterwards (PIN-210).
 */

import { useCallback, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { notification } from 'antd';

export const useStartSession = () => {
  const token = useSelector((state) => state.token);
  const [isStarting, setIsStarting] = useState(false);

  /*
    Holds a session created by a call whose startTest then failed. Retrying
    reuses it rather than creating another: an unstarted session is invisible to
    the examiner (the session list shows only active ones), so a create-per-retry
    leaves a trail of orphans nothing can surface.
  */
  const pendingSessionID = useRef(null);

  const start = useCallback(
    async ({ examineeID, recordLabel, testID }) => {
      const config = {
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Token ' + token
        }
      };

      setIsStarting(true);

      try {
        if (!pendingSessionID.current) {
          const created = await axios.post(
            process.env.REACT_APP_API_URL + '/session/create',
            {},
            config
          );

          const sessionID = created && created.data && created.data.sessionID;
          if (!sessionID) throw new Error('The server did not return a session.');
          pendingSessionID.current = sessionID;
        }

        const sessionID = pendingSessionID.current;
        const response = await axios.post(
          process.env.REACT_APP_API_URL + '/session/startTest',
          { examineeID, recordLabel, sessionID, testID },
          config
        );

        // Started successfully — the next attempt must not reuse this session.
        pendingSessionID.current = null;
        return { sessionID, mode: response && response.data && response.data.mode };
      } catch (error) {
        // Leaving the examiner staring at a stopped spinner with no explanation
        // is how a server outage reads as "the button is broken".
        console.log(error);
        notification['error']({
          message: 'Could not start the test.',
          description:
            (error && error.response && error.response.data && error.response.data.message) ||
            'The session could not be started. Please check your connection and try again.'
        });
        setIsStarting(false);
        return null;
      }
    },
    [token]
  );

  // isStarting deliberately stays true after success: the caller navigates away,
  // and re-enabling the button first invites a second submission.
  return { isStarting, start };
};
